import { lighten } from '@mui/material/styles';
import { createTheme as createMuiTheme } from '@mui/material/styles';
import merge from 'lodash/merge';

// `augmentColor` mutated its argument in v4 and returns in v5+, taking
// `{ color }` rather than the colour itself - calling it the old way throws
// MUI error #11 while the theme is being built, which is before anything
// renders: the page came up blank with no React tree at all.
//
// TODO: Overall theme should be refactored so stylings start including theme.palette.augmentColor.
// https://github.com/mui-org/material-ui/issues/12390


const rawTheme = createMuiTheme({
  palette: {
    primary: {
      light: '#F6B692',  // '#FEBD3C',
      main: '#e96316',
      dark: '#C0152E', //'#d87715',
      contrastText: '#EEEEEE'
    },
    secondary: {
      light: '#8c8c8c',
      main: '#5F5F5F',
      dark: '#353535',
      contrastText: '#EEEEEE'
    },
    // `type` in v4, `mode` in v5+. The old key is not an error, it is simply
    // not read - which is why it survived a rename that never happened.
    mode: 'light',
    // v5 moved secondary text from 0.54 to 0.6. Seventeen elements read it.
    text: { secondary: 'rgba(0, 0, 0, 0.54)' },
    background: {
      default: '#FFF'
    }
  },
  typography: {
    // A string, not an array. MUI 4 accepted the array and joined it; MUI 9
    // hands the value to emotion, which does not, and the whole application
    // fell back to the browser's generic `sans-serif` - 378 of the 492
    // elements compared. `useNextVariants` went with it: it was a flag for
    // migrating v3 to v4 and has meant nothing since.
    fontFamily: '"Gotham", "Open Sans", Helvetica, Arial, sans-serif'
  }
});

let blue = { main: '#00BFFF' };
blue = rawTheme.palette.augmentColor({ color: blue });

let success = { main:'#228a4f' };
success = rawTheme.palette.augmentColor({ color: success });

let warning = { main: '#FF8000' };
warning = rawTheme.palette.augmentColor({ color: warning });

let error = { main: '#FF0000' };
error = rawTheme.palette.augmentColor({ color: error });

let rowErrorBackground = { main: '#ff4033' };
rowErrorBackground = rawTheme.palette.augmentColor({ color: rowErrorBackground });

let background = {
  main: rawTheme.palette.secondary.main,
  light: rawTheme.palette.secondary.light,
  veryLight: lighten(rawTheme.palette.secondary.light, 0.7),
};
background = rawTheme.palette.augmentColor({ color: background });

/*
 * Three defaults MUI changed between 4 and 9, put back.
 *
 * This branch upgrades libraries and promises the screens do not change, so a
 * default that moved is a difference to undo rather than a new look to accept.
 * Each was measured by comparing the computed styles of every visible element
 * on three screens against the previous build - 492 in common - not guessed
 * from a changelog.
 *
 *   body font size   v4's CssBaseline dressed `body` in body2, v5+ uses body1:
 *                    14px became 16px on the <html> down, which moved 250
 *                    elements and, through them, most of the line-heights and
 *                    heights that follow from a font size.
 *   dense cell       `6px 24px 6px 16px` became `6px 16px` - 208 cells.
 *   small icon       3px of padding became 5px - 100 buttons.
 */
const muiV4Defaults = {
  // v5 changed the default variant of every input from `standard` - a label over
  // an underline - to `outlined`, a label notched into a box. Nothing in this
  // application asks for a variant, so everything drawn without one changed
  // shape: the login fields most visibly, and every lookup in a table's edit row.
  MuiTextField: { defaultProps: { variant: 'standard' } },
  MuiFormControl: { defaultProps: { variant: 'standard' } },
  MuiSelect: { defaultProps: { variant: 'standard' } },
  // v4's ExpansionPanelDetails had `8px 24px 24px`; the v5 rename to
  // AccordionDetails also changed the padding to `8px 16px 16px`. Sixteen pixels,
  // which every table inside a panel inherits: on /usersmanagement the container
  // measured 1676 where it had measured 1660, and since that number is what the
  // column widths are a share of, every column came out ~2px wider.
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: '8px 24px 24px',
      },
    },
  },
  // Same rename, same trim: v4's ExpansionPanelSummary had `0 24px`.
  //
  // And its expand icon was an IconButton - 48px wide, with `marginRight: -12`
  // to pull it back over the panel's own padding. v9 uses a plain 24px span, so
  // the summary's content box is 12px wider, and a heading whose width is
  // declared as `33.33%` of it starts 4px further along.
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: '0 24px',
      },
      expandIconWrapper: {
        padding: 12,
        marginRight: -12,
      },
    },
  },
  MuiCssBaseline: {
    // Size and line height, and NOT letter spacing. v4's body2 declares
    // `0.01071em`, but this application never showed it: measured on the
    // previous build, `body` computes to `letter-spacing: normal`. Adding it
    // from the documentation put 0.15px between every pair of letters in the
    // application - which is what "the words look too spaced out" was.
    //
    // Which is the rule this whole exercise keeps teaching: the previous build
    // is the specification, not the changelog.
    styleOverrides: {
      body: {
        fontSize: '0.875rem',
        lineHeight: 1.43,
      },
    },
  },
  MuiInputBase: {
    // v4's InputBase sat on `1.1875em` of line height and v5 on `1.4375em`:
    // 19px against 23px, which makes the field 36px tall instead of 32 and
    // drops its orange underline two pixels.
    styleOverrides: { root: { lineHeight: '1.1875em' } }
  },
  MuiInput: {
    // v4's input sat on `6px 0 7px`, v5's on `4px 0 5px`: two pixels of height
    // on every field in the application.
    // v4: height 1.1875em, padding 6px 0 7px. v5: 1.4375em and 4px 0 5px.
    // Nineteen pixels of content against twenty-three, which is a 32px field
    // against a 36px one - and the orange underline sits at the bottom of it.
    styleOverrides: { input: { height: '1.1875em', padding: '6px 0 7px' } }
  },
  MuiInputAdornment: {
    // v5 dresses an adornment in text.secondary; v4 left it at text.primary.
    styleOverrides: { root: { color: 'rgba(0, 0, 0, 0.87)' } }
  },
  MuiTableCell: {
    styleOverrides: {
      sizeSmall: {
        padding: '6px 24px 6px 16px',
        // The exceptions v4's rule carried with it. Without them the override
        // reaches cells that had no padding at all - the checkbox and the
        // detail-panel toggle - and a row that was 33px tall becomes 43.
        '&.MuiTableCell-paddingNone': { padding: 0 },
        '&.MuiTableCell-paddingCheckbox': { padding: '0 12px 0 16px' },
        // NOT a `:last-child` rule. v4 had one, but it applied on top of a cell
        // that already had padding; applied to `padding: 0` cells - the last
        // column is the detail-panel toggle - it puts 16px where there were
        // none. Twenty-five cells, and the reason the first attempt at this
        // moved them.
        '&:last-child:not(.MuiTableCell-paddingNone)': { paddingRight: 16 },
      },
    },
  },
  MuiIconButton: {
    // 3px small, 12px default: v9 uses 5 and 8. Nine buttons and six heights.
    styleOverrides: { root: { padding: 12 }, sizeSmall: { padding: 3 } }
  },
};

const theme = merge(rawTheme, {
  palette: { blue, error, success, warning, background, rowErrorBackground },
  components: muiV4Defaults
});

console.log('theme', theme);
export default theme;
